<template>
  <OnClickOutside :options="clickOutsideOptions" @trigger="hideTooltip">
    <div ref="anchor" class="grid">
      <!-- @slot Element on which the tooltip will be applied -->
      <slot :toggle-tooltip="toggleTooltipVisible" />
      <Teleport to="body" :disabled="!floating">
        <div
          v-if="tooltipVisible"
          data-hit-tooltip
          class="hit-tooltip-panel p-4 w-72 text-left inline-block tooltip-text bg-panel border border-panel rounded-lg shadow-lg"
          :class="tooltipClass"
          :style="floating ? tooltipStyle : undefined"
        >
          <div class="inline-grid grid-flow-row gap-2">
            <div class="block absolute top-0 right-0 pt-4 pr-4">
              <hit-icon
                icon="clear"
                :clickable="true"
                color="text-panel"
                class="focus:outline-none transition ease-in-out duration-150"
                @click.stop="hideTooltip"
              />
            </div>
            <div class="text-lg leading-6 font-medium text-panel mr-6">
              {{ title }}
            </div>
            <div
              v-hit-sanitized-html="content"
              class="text-sm leading-5 text-panel-light"
            />
          </div>
        </div>
      </Teleport>
    </div>
  </OnClickOutside>
</template>

<script>
import HitIcon from '../icon/HitIcon.vue';
import {OnClickOutside} from '@vueuse/components';
import {HitBreakpointsMixin} from '../../mixins';

const TOOLTIP_WIDTH_PX = 288; // w-72
const TOOLTIP_GAP_PX = 8;

export default {
  name: 'HitTooltip',
  components: {OnClickOutside, HitIcon},
  mixins: [HitBreakpointsMixin],
  props: {
    /**
     * Title of the tooltip
     */
    title: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Content of the tooltip
     */
    content: {
      type: String,
      required: true,
      default: '',
    },
    /**
     * Render the tooltip in a Teleport to body with fixed positioning.
     * Use this when ancestors clip overflow (e.g. sidebars next to a map).
     */
    floating: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Prefer opening the tooltip to the left of the trigger.
     * When floating, falls back to the right if there is not enough space.
     */
    preferLeft: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      tooltipVisible: false,
      tooltipStyle: {},
    };
  },
  computed: {
    clickOutsideOptions() {
      return this.floating ? {ignore: ['.hit-tooltip-panel']} : undefined;
    },
    tooltipClass() {
      if (this.floating) {
        return 'fixed z-[200]';
      }
      return {
        '-mt-8 ml-6 absolute z-50':
          this.responsiveBreakpointSm && !this.preferLeft,
        '-mt-8 mr-6 absolute z-50 right-full':
          this.responsiveBreakpointSm && this.preferLeft,
        'left-3 mt-8 fixed z-50': !this.responsiveBreakpointSm,
      };
    },
  },
  beforeUnmount() {
    this.detachPositionListeners();
  },
  methods: {
    toggleTooltipVisible() {
      this.tooltipVisible = !this.tooltipVisible;
      if (this.tooltipVisible && this.floating) {
        this.$nextTick(() => {
          this.updateFloatingPosition();
          this.attachPositionListeners();
        });
      } else {
        this.detachPositionListeners();
      }
      /**
       * When the visibility of the tooltip changes. True if visible, false if not.
       */
      this.$emit('tooltipVisible', this.tooltipVisible);
    },
    hideTooltip() {
      this.tooltipVisible = false;
      this.detachPositionListeners();
      /**
       * When the visibility of the tooltip changes. True if visible, false if not.
       */
      this.$emit('tooltipVisible', false);
    },
    updateFloatingPosition() {
      const anchor = this.$refs.anchor;
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const openLeft =
        this.preferLeft ||
        rect.right + TOOLTIP_GAP_PX + TOOLTIP_WIDTH_PX > window.innerWidth;
      const left = openLeft
        ? Math.max(
            TOOLTIP_GAP_PX,
            rect.left - TOOLTIP_WIDTH_PX - TOOLTIP_GAP_PX,
          )
        : rect.right + TOOLTIP_GAP_PX;
      const top = Math.max(TOOLTIP_GAP_PX, rect.top - 8);
      this.tooltipStyle = {
        top: `${top}px`,
        left: `${left}px`,
      };
    },
    attachPositionListeners() {
      this.detachPositionListeners();
      this._onReposition = () => this.updateFloatingPosition();
      window.addEventListener('scroll', this._onReposition, true);
      window.addEventListener('resize', this._onReposition);
    },
    detachPositionListeners() {
      if (!this._onReposition) {
        return;
      }
      window.removeEventListener('scroll', this._onReposition, true);
      window.removeEventListener('resize', this._onReposition);
      this._onReposition = null;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss"></style>

<style lang="scss">
.tooltip-text {
  text-transform: none;
  ul {
    padding-left: 40px;

    li {
      padding-left: initial;
    }
  }
}
</style>
